import { format, isToday } from 'date-fns';
import { useMemo } from 'react';
import { TimeSlotEntry } from '../types/booking';
import { extractDateFromISOString } from '../utils/dates';

interface UseCalendarParams {
	timeSlots: TimeSlotEntry[];
	locale: string;
	month: number;
	year: number;
}

interface CalendarDay {
	date: string;
	dayNumber: string;
	isAvailable: boolean;
	isToday: boolean;
}

export const useCalendar = ({ timeSlots, locale, month, year }: UseCalendarParams) => {
	const availableDates = useMemo(() => {
		return new Set(timeSlots.map((timeSlot) => extractDateFromISOString(timeSlot.start_datetime)));
	}, [timeSlots]);

	// Memoized function to get weekdays and offset for the selected month and year
	const { entries, weekdayOffset } = useMemo(() => {
		const entries: CalendarDay[] = [];
		let firstWeekdayInMonth: number | null = null;
		const daysInCurrentMonth = new Date(year, month + 1, 0).getDate();

		for (let day = 1; day <= daysInCurrentMonth; day++) {
			const date = new Date(year, month, day);
			const weekday = date.getDay();

			// Only include weekdays (Monday to Friday)
			if (weekday >= 1 && weekday <= 5) {
				if (firstWeekdayInMonth === null) {
					firstWeekdayInMonth = weekday;
				}

				const formattedDate = format(date, 'yyyy-MM-dd');

				entries.push({
					date: formattedDate,
					dayNumber: date.toLocaleString(locale, { day: 'numeric' }),
					isAvailable: availableDates.has(formattedDate),
					isToday: isToday(date),
				});
			}
		}

		const weekdayOffset = firstWeekdayInMonth !== null ? firstWeekdayInMonth - 1 : 0; // 1 = Monday, 2 = Tuesday, ...

		return { entries, weekdayOffset };
	}, [availableDates, locale, month, year]);

	return { entries, weekdayOffset };
};
